(ns app.front.view.workspace.workspace-labels
  (:require [app.domain.label.model :as label-model]
            [app.domain.util :as du]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.label-group :as label-ui]
            [app.front.ui.label-group-state :as label-group-state]
            [app.front.util :refer [for-frag]]
            [app.front.view.common.label-file-transfer :as label-file-transfer]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; ============================================================================
;; State Management (workspace-specific, UI state uses label-state)
;; ============================================================================

(rf/reg-event-db ::set-add-groups-open
  (fn [db [_ open?]]
    (assoc db ::add-groups-open? open?)))

(rf/reg-sub ::add-groups-open?
  :-> #(get % ::add-groups-open? false))

(rf/reg-event-db ::set-selected-predefined
  (fn [db [_ group-id selected?]]
    (if selected?
      (update db ::selected-predefined (fnil conj #{}) group-id)
      (update db ::selected-predefined (fnil disj #{}) group-id))))

(rf/reg-sub ::selected-predefined
  :-> #(get % ::selected-predefined #{}))

(rf/reg-event-db ::set-new-group-input-focused
  (fn [db [_ focused?]]
    (assoc db ::new-group-input-focused focused?)))

(rf/reg-sub ::new-group-input-focused?
  :-> #(get % ::new-group-input-focused false))

;; ============================================================================
;; Options Panel State (for inline panel within Options)
;; ============================================================================

;; The panel needs no data of its own: the label groups the workspace owns arrive with
;; :workspace/get, and the authority group's groups (the templates offered for assigning) with
;; :workspace/get-config. The phase entity is stored because every change is a signal applied to it.
(rf/reg-event-db ::open-panel
  (fn [db [_ target-id]]
    (-> db
        (assoc ::panel-open? true)
        (assoc ::signal-target-id target-id))))

(rf/reg-event-db ::close-panel
  (fn [db _]
    (-> db
        (assoc ::panel-open? false)
        (assoc ::add-groups-open? false)
        (dissoc ::selected-predefined)
        (dissoc ::signal-target-id)
        (dissoc ::label-group-state/editing-group-id)
        (dissoc ::label-group-state/editing-group-name))))

(rf/reg-sub ::panel-open?
  :-> #(get % ::panel-open? false))

(rf/reg-sub ::signal-target-id
  :-> ::signal-target-id)

;; ============================================================================
;; Signals - Workspace Label Groups
;; ============================================================================
;; Every change goes through the workspace's state machine, so the workspace's own guards and
;; permission checks apply, and labels are frozen once the workspace is archived.

(rf/reg-event-fx ::signal
  (fn [{:keys [db]} [_ signal params & [opts]]]
    (if-let [target-id (::signal-target-id db)]
      {:dispatch [::process/signal
                  target-id
                  [signal params]
                  (merge {:notify {:success :changes-saved}} opts)]}
      ;; ::open-panel stores the phase entity; without it there is nothing to apply the signal to,
      ;; so fail loudly rather than dropping the user's edit silently.
      (do
        (js/console.error "::signal - the label panel has no ::signal-target-id" signal)
        {:dispatch [:notifications/add :toast :error :error/error :error/unknown]}))))

(rf/reg-event-fx ::create-workspace-label-group
  (fn [_ [_ group-name]]
    {:dispatch [::signal
                :process.signal/create-workspace-label-group
                {:name group-name}
                {:on-success [::create-label-group.success]}]}))

(rf/reg-event-fx ::create-label-group.success
  (fn [_ _]
    {:dispatch-n [[::label-group-state/cancel-editing-group]
                  [::label-group-state/cancel-adding-group]]}))

(rf/reg-event-fx ::update-label-group-name
  (fn [_ [_ group-id new-name]]
    {:dispatch [::signal
                :process.signal/edit-workspace-label-group
                {:label-group-id group-id
                 :name           new-name}
                {:on-success [::label-group-state/cancel-editing-group]}]}))

(rf/reg-event-fx ::delete-label-group
  (fn [_ [_ group-id]]
    {:dispatch [::signal
                :process.signal/delete-workspace-label-group
                {:label-group-id group-id}]}))

(rf/reg-event-fx ::add-label
  (fn [_ [_ group-id label-name]]
    {:dispatch [::signal
                :process.signal/create-workspace-label
                {:label-group-id group-id
                 :name           label-name}
                {:on-success [::label-group-state/clear-new-label-input group-id]}]}))

(rf/reg-event-fx ::update-label
  (fn [_ [_ label-id new-name]]
    {:dispatch [::signal
                :process.signal/edit-workspace-label
                {:label-id label-id
                 :name     new-name}
                {:on-success [::label-group-state/cancel-editing-label]}]}))

(rf/reg-event-fx ::delete-label
  (fn [_ [_ label-id]]
    {:dispatch [::signal
                :process.signal/delete-workspace-label
                {:label-id label-id}]}))

(rf/reg-event-fx ::assign-label-groups
  (fn [_ [_ label-group-ids]]
    (when (seq label-group-ids)
      {:dispatch [::signal
                  :process.signal/assign-workspace-label-groups
                  {:label-group-ids label-group-ids}]})))

(rf/reg-event-fx ::add-predefined-groups
  (fn [{:keys [db]} [_ group-ids]]
    (cond-> {:db (-> db
                     (dissoc ::selected-predefined)
                     (assoc ::add-groups-open? false))}
      (seq group-ids)
      (assoc :dispatch [::assign-label-groups (vec group-ids)]))))

;; ============================================================================
;; Data Subscriptions
;; ============================================================================

(rf/reg-sub ::workspace-label-groups-raw
  :<- [::ws-subs/workspace]
  (fn [workspace _]
    (->> (label-model/active-workspace-label-groups workspace)
         (sort-by label-model/label-group-name))))

(rf/reg-sub ::workspace-label-groups
  :<- [::workspace-label-groups-raw]
  (fn [workspace-labels _]
    (->> workspace-labels
         (map (fn [group]
                {:id        (:db/id group)
                 :name      (label-model/label-group-name group)
                 :labels    (->> (label-model/active-labels group)
                                 (map (fn [lbl]
                                        {:id   (:db/id lbl)
                                         :name (label-model/label-name lbl)}))
                                 vec)
                 ;; In use on the workspace, as opposed to merely owned by it.
                 :assigned? (du/in-state? :label-group.state/assigned group)
                 :source    :workspace}))
         vec)))

;; The groups still offerable for assigning. Assigning is not idempotent for authority groups —
;; :process.action/assign-workspace-label-groups copies the template under its own name, so a template
;; whose name the workspace already holds is not offered: the action rejects it with
;; :labels/group-name-taken, and offering a checkbox that can only fail is worse than hiding it. A
;; workspace group already in use is dropped for a plainer reason: it is listed in the main view.
(rf/reg-sub ::predefined-label-groups
  (fn [[_ authority-group-id]]
    [(rf/subscribe [::label-group-state/label-groups authority-group-id])
     (rf/subscribe [::workspace-label-groups])])
  (fn [[authority-labels workspace-labels] _]
    (let [taken-names      (into #{}
                                 (map (comp label-model/normalized-label-name :name))
                                 workspace-labels)
          authority-groups (->> authority-labels
                                (remove #(contains? taken-names
                                                    (-> (label-model/label-group-name %)
                                                        label-model/normalized-label-name)))
                                (map (fn [group]
                                       {:id     (:db/id group)
                                        :name   (label-model/label-group-name group)
                                        :labels (->> (label-model/active-labels group)
                                                     (map (fn [lbl]
                                                            {:id   (:db/id lbl)
                                                             :name (label-model/label-name lbl)}))
                                                     vec)
                                        :source :authority-group}))
                                vec)]
      {:workspace-groups (into [] (remove :assigned?) workspace-labels)
       :authority-groups authority-groups})))

(rf/reg-sub ::assigned-label-groups
  :<- [::ws-subs/workspace]
  (fn [workspace _]
    (->> (label-model/active-assigned-label-groups workspace)
         (sort-by label-model/label-group-name)
         vec)))

;; ============================================================================
;; Connected Components - Wire shared UI to local state
;; ============================================================================

(defn- deletable-label-chip
  "Label chip with delete confirmation, connected to local state"
  [{:keys [tr label existing-names]}]
  (r/with-let [*show-delete-modal? (r/atom false)]
    (let [label-id     (:id label)
          label-name   (:name label)
          editing-id   (<sub [::label-group-state/editing-label-id])
          editing?     (= editing-id label-id)
          editing-name (<sub [::label-group-state/editing-label-name])]
      [:<>
       [ui/confirm-modal
        {:open?        @*show-delete-modal?
         :tr           tr
         :id           (str "delete-label-" label-id)
         :on-yes-click (fn []
                         (>evt [::delete-label label-id])
                         (reset! *show-delete-modal? false))
         :on-close     #(reset! *show-delete-modal? false)}
        (tr [:labels/confirm-delete-label])]
       [label-ui/label-chip
        {:tr             tr
         :label          {:id label-id :name label-name}
         :editing?       editing?
         :editing-name   editing-name
         :existing-names existing-names
         :current-id     label-id
         :on-click       #(>evt [::label-group-state/set-editing-label label-id label-name])
         :on-change      #(>evt [::label-group-state/set-editing-label-name %])
         :on-save        #(>evt [::update-label label-id editing-name])
         :on-cancel      #(>evt [::label-group-state/cancel-editing-label])
         :on-delete      #(reset! *show-delete-modal? true)}]])))

(defn- new-label-input-connected
  "New label input connected to local state"
  [{:keys [tr group-id existing-names]}]
  (let [value         (<sub [::label-group-state/new-label-name group-id])
        focused?      (<sub [::label-group-state/input-focused? group-id])
        show-buttons? (or focused? (seq value))]
    [label-ui/new-label-input
     {:id             (str "new-label-input-" group-id)
      :tr             tr
      :value          value
      :existing-names existing-names
      :show-buttons?  show-buttons?
      :on-change      #(>evt [::label-group-state/set-new-label-name group-id %])
      :on-focus       #(>evt [::label-group-state/set-input-focused group-id true])
      :on-blur        #(>evt [::label-group-state/set-input-focused group-id false])
      :on-save        #(>evt [::add-label group-id value])
      :on-cancel      #(>evt [::label-group-state/set-new-label-name group-id ""])}]))

(defn- label-group-section
  "A collapsible section for a label group"
  [{:keys [tr group]}]
  (r/with-let [*show-delete-group-modal? (r/atom false)]
    (let [group-id       (:id group)
          group-name     (:name group)
          labels         (:labels group)
          existing-names (into {} (map (juxt :name :id)) labels)
          expanded?      (<sub [::label-group-state/group-expanded? group-id])]
      [:<>
       [ui/confirm-modal
        {:open?        @*show-delete-group-modal?
         :tr           tr
         :id           (str "delete-group-" group-id)
         :modal-title  (tr [:labels/confirm-delete-group])
         :on-yes-click (fn []
                         (>evt [::delete-label-group group-id])
                         (reset! *show-delete-group-modal? false))
         :on-close     #(reset! *show-delete-group-modal? false)}
        (tr [:labels/confirm-delete-group-body])]
       [:div.mb-2
        [label-ui/label-group-header
         {:tr          tr
          :group       {:id group-id :name group-name}
          :label-count (count labels)
          :expanded?   expanded?
          :on-toggle   #(>evt [::label-group-state/toggle-group-expanded group-id])
          :on-edit     #(>evt [::label-group-state/set-editing-group group-id group-name])
          :on-delete   #(reset! *show-delete-group-modal? true)}]
        (when expanded?
          [:div
           [:div.flex.flex-wrap
            (for-frag [label labels]
              ^{:key (:id label)}
              [deletable-label-chip {:tr             tr
                                     :label          label
                                     :existing-names existing-names}])]
           [new-label-input-connected {:tr             tr
                                       :group-id       group-id
                                       :existing-names existing-names}]])]])))

(defn- add-new-group-input
  "Input field for creating a new custom group"
  [{:keys [tr existing-names]}]
  (let [value         (<sub [::label-group-state/new-group-name])
        focused?      (<sub [::new-group-input-focused?])
        show-buttons? (or focused? (seq value))]
    [label-ui/new-label-input
     {:id             "new-group-input"
      :tr             tr
      :placeholder    (tr [:labels/new-group-name])
      :value          value
      :existing-names existing-names
      :conflict-text  (tr [:labels/group-name-taken])
      :show-buttons?  show-buttons?
      :on-change      #(>evt [::label-group-state/set-new-group-name %])
      :on-focus       #(>evt [::set-new-group-input-focused true])
      :on-blur        #(>evt [::set-new-group-input-focused false])
      :on-save        #(>evt [::create-workspace-label-group value])
      :on-cancel      #(>evt [::label-group-state/cancel-adding-group])}]))

(defn- group-name-edit-modal-connected
  "Group name edit modal connected to local state"
  [{:keys [tr]}]
  (let [editing-id     (<sub [::label-group-state/editing-group-id])
        editing-name   (<sub [::label-group-state/editing-group-name])
        ;; Every group the workspace owns holds its name, in use or not — that is what
        ;; :process.action/edit-workspace-label-group checks against.
        existing-names (into {} (map (juxt label-model/label-group-name :db/id))
                             (<sub [::workspace-label-groups-raw]))
        open?          (some? editing-id)]
    [label-ui/group-name-edit-modal
     {:tr             tr
      :open?          open?
      :value          editing-name
      :existing-names existing-names
      :current-id     editing-id
      :on-change      #(>evt [::label-group-state/set-editing-group-name %])
      :on-save        #(>evt [::update-label-group-name editing-id editing-name])
      :on-close       #(>evt [::label-group-state/cancel-editing-group])}]))


;; ============================================================================
;; Predefined Groups Selection
;; ============================================================================

(defn- predefined-group-checkbox
  "Checkbox for selecting a predefined label group"
  [{:keys [group selected? on-change]}]
  [:div.flex.items-center.gap-2.py-1
   [cc/checkbox
    {:id         (str "predefined-" (:id group))
     :checked    selected?
     :label-text (:name group)
     :on-change  #(on-change (not selected?))}]])

;; ============================================================================
;; Main Views
;; ============================================================================

(defn- add-groups-content
  "Content for adding predefined or new custom groups"
  [{:keys [tr]}]
  (let [authority-group-id         (<sub [::ws-subs/authority-group-id])
        {:keys [workspace-groups
                authority-groups]} (<sub [::predefined-label-groups authority-group-id])
        selected                   (<sub [::selected-predefined])
        ;; Not `workspace-groups`: that holds the groups offerable for assigning, which is a smaller
        ;; set than the names the workspace holds.
        owned-names                (into {} (map (juxt label-model/label-group-name :db/id))
                                         (<sub [::workspace-label-groups-raw]))]
    [:<>
     [:div.border.border-solid.border-blue-600.rounded.bg-blue-100.px-4.pt-4
      ;; Authority group (predefined) labels section
      [:div.font-medium.mb-2 (tr [:labels/predefined-labels])]
      [:div.mb-4
       (for-frag [group authority-groups]
         [predefined-group-checkbox
          {:group     group
           :selected? (contains? selected (:id group))
           :on-change #(>evt [::set-selected-predefined (:id group) %])}])
       [:hr.border-gray-100]
       ;; Workspace-specific labels section: only the groups not already in use are offerable,
       ;; the ones in use are listed in the main view.
       (for-frag [group workspace-groups]
         [predefined-group-checkbox
          {:group     group
           :selected? (contains? selected (:id group))
           :on-change #(>evt [::set-selected-predefined (:id group) %])}])
       ;; workspace group creation section. Every owned name is taken, in use or not — that is what
       ;; :process.action/create-workspace-label-group rejects.
       [add-new-group-input {:tr             tr
                             :existing-names owned-names}]]]

     [:div.mt-6.flex.gap-2
      [ui/button
       {:kind     :primary
        :size     :small
        :disabled (not (seq selected))
        :on-click #(>evt [::add-predefined-groups selected])}
       (tr [:labels/done])]
      [ui/button
       {:kind     :secondary
        :size     :small
        :on-click #(>evt [::set-add-groups-open false])}
       (tr [:cancel])]]]))

(defn- groups-list-content
  "Content showing label groups assigned to this workspace"
  [{:keys [tr label-groups]}]
  [:div
   ;; Display label groups with their labels
   (for-frag [group label-groups]
     ^{:key (:id group)}
     [label-group-section {:tr tr :group group}])

   [:div.mt-2
    [ui/button
     {:kind     :secondary
      :size     :small
      :on-click #(>evt [::set-add-groups-open true])}
     (tr [:labels/add-group])]]
   ;; Edit group name modal
   [group-name-edit-modal-connected {:tr tr}]])

(defn full-management-view
  "Full label management view - single entry point for all label management"
  [{:keys [tr]}]
  (let [groups-open?         (<sub [::add-groups-open?])
        workspace-id         (<sub [::ws-subs/workspace-id])
        transfer-context     (assoc label-file-transfer/workspace-context
                                    :parent-id-value workspace-id
                                    :signal-target-id (<sub [::signal-target-id]))
        label-group-entities (<sub [::assigned-label-groups])
        label-groups         (->> label-group-entities
                                  (map (fn [group]
                                         {:id     (:db/id group)
                                          :name   (label-model/label-group-name group)
                                          :labels (->> (label-model/active-labels group)
                                                       (map (fn [lbl]
                                                              {:id   (:db/id lbl)
                                                               :name (label-model/label-name lbl)}))
                                                       (sort-by :name)
                                                       vec)}))
                                  vec)
        import-in-progress?  (<sub [::label-file-transfer/import-in-progress?])
        export-in-progress?  (<sub [::label-file-transfer/export-in-progress?])
        ;; Every group the workspace owns holds its name, in use or not — that is what
        ;; :process.action/create-workspace-label-group checks against, so an import of a name held by
        ;; a merely owned group has to raise the rename-conflict modal rather than reach the server.
        existing-names       (into {} (map (juxt label-model/label-group-name :db/id))
                                   (<sub [::workspace-label-groups-raw]))]
    [:div.m-4
     [:div.text-gray-600.mb-4 (tr [:labels/description])]
     [:div
      [:div
       {:class (str "transition-all duration-500 "
                    (if groups-open? "opacity-0 h-0 overflow-hidden" "opacity-100"))}
       [groups-list-content (util/mmap tr label-groups)]]
      [:div
       {:class (str "transition-all duration-500 "
                    (if groups-open? "opacity-100" "opacity-0 h-0 overflow-hidden"))}
       [add-groups-content {:tr tr}]]]
     [:hr.mt-4.border-gray-100]
     [:div.flex.mt-4
      [label-file-transfer/import-button
       {:tr             tr
        :kind           :ghost
        :size           :small
        :class-name     "!text-xs !px-1"
        :context        transfer-context
        :existing-names existing-names
        :disabled?      import-in-progress?}]
      (when-not groups-open?
        (cc/button {:kind       :ghost
                    :size       :small
                    :class-name "!text-xs !px-1"
                    :disabled   (or export-in-progress? (empty? label-group-entities))
                    :on-click   #(>evt [::label-file-transfer/open-export-modal])}
                   (tr [:labels/export-labels])))]
     ;; Export modal
     [label-file-transfer/export-modal {:tr           tr
                                        :label-groups label-group-entities
                                        :context      transfer-context}]

     ;; Import conflict modal
     [label-file-transfer/import-conflict-modal {:tr      tr
                                                 :context transfer-context}]]))

;; ============================================================================
;; Options Panel Content (embedded in Options panel)
;; ============================================================================

(defn options-panel-content
  "Content rendered inside the Options panel when Label Management is open"
  [tr]
  [:div.flex.flex-col.h-full
   [full-management-view {:tr tr}]])
